/**
 * Logger central para la aplicación
 *
 * En desarrollo: todos los niveles se loguean normalmente
 * En producción: solo warnings y errores
 *
 * Evita efectos secundarios en argumentos de logging
 */

export type LogLevel = "debug" | "info" | "warn" | "error";

interface LoggerConfig {
  level: LogLevel;
  isDevelopment: boolean;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

class Logger {
  private config: LoggerConfig;

  constructor() {
    this.config = {
      level: (process.env.NODE_ENV === "development"
        ? "debug"
        : "warn") as LogLevel,
      isDevelopment: process.env.NODE_ENV === "development",
    };
  }

  private shouldLog(level: LogLevel): boolean {
    if (level === "debug" && !this.config.isDevelopment) {
      return false;
    }
    return LOG_LEVELS[level] >= LOG_LEVELS[this.config.level];
  }

  private formatMessage(
    level: LogLevel,
    message: string
  ): string {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;
    return `${prefix} ${message}`;
  }

  private safeStringify(obj: unknown): string {
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return "[Object cannot be stringified]";
    }
  }

  private processArgs(args: unknown[]): unknown[] {
    return args.map((arg) => {
      if (typeof arg === "object" && arg !== null) {
        try {
          // Crear una copia segura del objeto para evitar efectos secundarios
          return JSON.parse(this.safeStringify(arg));
        } catch {
          return arg;
        }
      }
      return arg;
    });
  }

  debug(message: string, ...args: unknown[]): void {
    if (!this.shouldLog("debug")) return;

    const processedArgs = this.processArgs(args);
    const formattedMessage = this.formatMessage("debug", message);

    if (this.config.isDevelopment) {
      console.log(formattedMessage, ...processedArgs);
    }
  }

  info(message: string, ...args: unknown[]): void {
    if (!this.shouldLog("info")) return;

    const processedArgs = this.processArgs(args);
    const formattedMessage = this.formatMessage("info", message);

    console.info(formattedMessage, ...processedArgs);
  }

  warn(message: string, ...args: unknown[]): void {
    if (!this.shouldLog("warn")) return;

    const processedArgs = this.processArgs(args);
    const formattedMessage = this.formatMessage("warn", message);

    console.warn(formattedMessage, ...processedArgs);
  }

  error(message: string, ...args: unknown[]): void {
    if (!this.shouldLog("error")) return;

    const processedArgs = this.processArgs(args);
    const formattedMessage = this.formatMessage("error", message);

    console.error(formattedMessage, ...processedArgs);
  }

  // Método para logging de errores con contexto adicional
  errorWithContext(
    context: string,
    error: unknown,
    additionalData?: Record<string, unknown>
  ): void {
    if (!this.shouldLog("error")) return;

    const errorMessage = error instanceof Error ? error.message : String(error);
    const stackTrace = error instanceof Error ? error.stack : undefined;

    const logData = {
      context,
      error: errorMessage,
      stack: stackTrace,
      ...additionalData,
    };

    this.error(`${context}: ${errorMessage}`, logData);
  }

  // Método para logging de operaciones con timing
  time(label: string): void {
    if (this.config.isDevelopment) {
      console.time(label);
    }
  }

  timeEnd(label: string): void {
    if (this.config.isDevelopment) {
      console.timeEnd(label);
    }
  }

  // Método para logging de grupos
  group(label: string): void {
    if (this.config.isDevelopment) {
      console.group(label);
    }
  }

  groupEnd(): void {
    if (this.config.isDevelopment) {
      console.groupEnd();
    }
  }
}

// Instancia singleton del logger
export const logger = new Logger();

// Exportar tipos para uso en otros archivos
export type { Logger };
